import { useState, useRef, useEffect } from 'react';
import { Music, VolumeX } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const TRACKS = {
  aura: 'https://www.youtube.com/embed/KDpRD0oZFUE?enablejsapi=1&autoplay=0',
  sprinter: 'https://www.youtube.com/embed/VJBsOLdeBM4?enablejsapi=1&autoplay=1',
  listeningTo: 'https://www.youtube.com/embed/pSY3i5XHHXo?enablejsapi=1&autoplay=1',
  chamakChallo: 'https://www.youtube.com/embed/oAVhUAaVCVQ?enablejsapi=1&autoplay=1',
  archives: 'https://www.youtube.com/embed/W-TE_Ys4iwM?enablejsapi=1&autoplay=1&start=12',
};

export default function MusicPlayer() {
  const [showPopup, setShowPopup] = useState(false);
  const [musicPlaying, setMusicPlaying] = useState(false);
  const [currentTrack, setCurrentTrack] = useState('aura');

  const playerRef = useRef(null);

  useEffect(() => {
    const seen = sessionStorage.getItem('aura_prompt_seen');
    if (!seen) {
      setTimeout(() => setShowPopup(true), 1200);
    }
  }, []);

  const sendCommand = (func) => {
    playerRef.current?.contentWindow?.postMessage(
      JSON.stringify({ event: 'command', func }),
      '*'
    );
  };

  const pauseMusic = () => {
    sendCommand('pauseVideo');
    setMusicPlaying(false);
  };

  const playAura = () => {
    setCurrentTrack('chamakChallo');
    setMusicPlaying(true);
  };

  const switchToSprinter = () => {
    setCurrentTrack('sprinter');
    setMusicPlaying(true);
  };

  const playListeningTo = () => {
    setCurrentTrack('listeningTo');
    setMusicPlaying(true);
  };

  const playArchives = () => {
    setCurrentTrack('archives');
    setMusicPlaying(true);
  };

  useEffect(() => {
    if (musicPlaying && currentTrack === 'chamakChallo') {
      const timer = setTimeout(() => {
        sendCommand('playVideo');
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [currentTrack, musicPlaying]);

  useEffect(() => {
    window.addEventListener('playSprinter', switchToSprinter);
    window.addEventListener('playListeningTo', playListeningTo);
    window.addEventListener('pauseListeningTo', pauseMusic);
    window.addEventListener('playArchives', playArchives);
    window.addEventListener('pauseArchives', pauseMusic);
    return () => {
      window.removeEventListener('playSprinter', switchToSprinter);
      window.removeEventListener('playListeningTo', playListeningTo);
      window.removeEventListener('pauseListeningTo', pauseMusic);
      window.removeEventListener('playArchives', playArchives);
      window.removeEventListener('pauseArchives', pauseMusic);
    };
  }, []);

  const handleYes = () => {
    sessionStorage.setItem('aura_prompt_seen', '1');
    setShowPopup(false);
    setTimeout(() => {
      sendCommand('playVideo');
      setMusicPlaying(true);
    }, 300);
  };

  const handleNo = () => {
    sessionStorage.setItem('aura_prompt_seen', '1');
    setShowPopup(false);
    setTimeout(() => {
      sendCommand('playVideo');
      setMusicPlaying(true);
    }, 300);
  };

  const toggleMusic = () => {
    if (musicPlaying) {
      sendCommand('pauseVideo');
    } else {
      sendCommand('playVideo');
    }
    setMusicPlaying(!musicPlaying);
  };

  return (
    <>
      {/* Off-screen YouTube iframe */}
      <iframe
        key={currentTrack}
        ref={playerRef}
        src={TRACKS[currentTrack]}
        style={{ position: 'fixed', top: '-9999px', left: '-9999px', width: '1px', height: '1px' }}
        allow="autoplay"
        title="bg-music"
        onLoad={() => {
          if (musicPlaying && currentTrack === 'chamakChallo') {
            setTimeout(() => {
              sendCommand('playVideo');
            }, 500);
          }
        }}
      />

      {/* Persistent toggle button */}
      <div className="fixed bottom-6 left-6 z-50 flex items-center gap-2">
        <button
          onClick={playAura}
          className="flex items-center gap-2 bg-card border border-primary/30 text-primary font-mono-cv text-xs px-3 py-2 rounded-xl hover:border-primary/60 hover:bg-primary/10 transition-all shadow-lg"
        >
          <Music size={14} />
          aura
        </button>
        <button
          onClick={toggleMusic}
          className="flex items-center gap-2 bg-card border border-primary/30 text-primary font-mono-cv text-xs px-3 py-2 rounded-xl hover:border-primary/60 hover:bg-primary/10 transition-all shadow-lg"
        >
          {musicPlaying ? <VolumeX size={14} /> : <Music size={14} />}
          {musicPlaying ? 'mute' : 'play'}
        </button>
      </div>

      {/* Welcome popup */}
      <AnimatePresence>
        {showPopup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] flex items-end justify-start p-6 pointer-events-none"
          >
            <motion.div
              initial={{ opacity: 0, y: 30, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.95 }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
              className="pointer-events-auto bg-card border border-primary/40 rounded-2xl px-6 py-5 shadow-2xl shadow-primary/20 max-w-xs"
            >
              <p className="font-bold text-foreground text-sm mb-1" style={{ fontFamily: 'Syne, sans-serif' }}>
                wanna experience some aura?
              </p>

              <div className="flex gap-3">
                <button
                  onClick={handleYes}
                  className="flex-1 bg-primary text-primary-foreground font-bold font-mono-cv text-xs py-2 rounded-lg hover:opacity-90 transition-all"
                >
                  +100 aura
                </button>
                <button
                  onClick={handleNo}
                  className="flex-1 border border-border text-muted-foreground font-mono-cv text-xs py-2 rounded-lg hover:border-primary/40 hover:text-foreground transition-all"
                >
                  no
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}